import React, { useCallback, useMemo } from "react";
import { Pressable, View } from "react-native";
import { generatePath, useNavigate } from "react-router";
import { useI18nMessage } from "@lookiero/i18n-react";
import { QuestionItem, QuestionType, useAnswersForQuestion } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Text } from "@lookiero/sty-psp-ui";
import { Icon } from "@lookiero/sty-psp-ui";
import { useViewScreenBySlugAndCustomerId } from "../../../../../projection/screen/react/useViewScreenBySlugAndCustomerId";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { EMPTY_ANSWER, useQuestionAnswers } from "../../behaviours/useQuestionAnswers";
import { style as hostScreenQuestionItemStyle } from "./HostScreenQuestionItem.style";

const HostScreenQuestionItem: QuestionItem<QuestionType.HOST_SCREEN> = ({ question, segment, country }) => {
  const {
    customer: { customerId },
  } = useStaticInfo();
  const navigate = useNavigate();

  const style = useMemo(() => hostScreenQuestionItemStyle(), []);

  const questionTitle = useI18nMessage({
    domain: DOMAIN,
    id: question.translationKey as string,
    values: { segment: segment.toLowerCase() },
  });

  const { answers } = useAnswersForQuestion({ question });
  const answer = useQuestionAnswers({ question, answers, country, segment });
  const showIconNotification = answer === EMPTY_ANSWER;

  const navigateToScreen = useCallback(
    () => navigate(generatePath(Routes.SCREEN, { slug: question.metadata.slug })),
    [navigate, question.metadata.slug],
  );

  /**
   * Pre-load screen projection so that when the Screen route is mounted, the projection is already fetch.
   */
  useViewScreenBySlugAndCustomerId({ customerId, slug: question.metadata.slug, experiment: undefined });

  return (
    <Pressable style={style.question} testID={question.id} onPress={navigateToScreen}>
      <View style={style.questionAndAnswer}>
        <Text ellipsizeMode="tail" level={1} variant="detail">
          {questionTitle}
        </Text>
        <Text ellipsizeMode="tail" level={1} style={style.answer} variant="detail">
          {answer}
        </Text>
      </View>
      <View style={style.iconContainer}>
        <Icon name="arrow_right_small" style={style.icon} />
        {showIconNotification && <View style={style.notification} testID="host-screen-question-item-notification" />}
      </View>
    </Pressable>
  );
};

export { HostScreenQuestionItem };
